import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Users, ChevronRight } from "lucide-react";

// Sprint 11 — minimal customer fields. Legacy cars/plates preserved in DB but hidden here.
const emptyCustomer = { name: "", phone: "", email: "", address: "", cars: [], plates: [], notes: "" };

export default function Customers() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCustomer);
  const qc = useQueryClient();

  const { data = [] } = useQuery({
    queryKey: ["customers", q],
    queryFn: async () => (await api.get(`/customers?q=${encodeURIComponent(q)}`)).data,
  });

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/customers", form)).data,
    onSuccess: () => {
      toast.success("Kunde erstellt");
      qc.invalidateQueries({ queryKey: ["customers"] });
      setOpen(false);
      setForm(emptyCustomer);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="stellar-page">
      <div className="stellar-page-heading flex flex-wrap items-center gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="stellar-module-icon stellar-module-indigo"><Users /></div>
            <h1 className="font-display text-2xl sm:text-4xl font-bold tracking-tight">Kunden</h1>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Verwalten Sie Ihre Kundendaten und Fahrzeuge.</p>
        </div>
        <div className="sm:ml-auto flex items-center gap-2 w-full sm:w-auto">
          <Input placeholder="Suchen…" value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 sm:w-64 bg-white" data-testid="customer-search" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-customer-btn" size="sm" className="sm:size-default rounded-xl bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline"> Neuer Kunde</span></Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Neuer Kunde</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="new-customer-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="new-customer-phone" /></div>
                  <div><Label>E-Mail</Label><Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="new-customer-email" /></div>
                </div>
                <div><Label>Adresse (optional)</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
                <div><Label>Notizen (optional)</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></div>
                <p className="text-[11px] text-muted-foreground">Fahrzeuge werden über <span className="font-medium">Fahrzeuge</span> erfasst, nicht am Kunden.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
                <Button onClick={() => createMut.mutate()} disabled={!form.name} data-testid="save-customer">Speichern</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="premium-card overflow-hidden">
        <table className="stellar-data-table w-full text-sm">
          <thead className="bg-slate-50/80 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Telefon</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">E-Mail</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {data.length === 0 && (
              <tr><td colSpan={4} className="text-center py-12 text-muted-foreground">Keine Kunden gefunden</td></tr>
            )}
            {data.map((c) => (
              <tr key={c.id} className="stellar-table-row">
                <td className="px-4 py-3">
                  <Link to={`/kunden/${c.id}`} data-testid={`customer-row-${c.id}`} className="font-medium hover:underline">{c.name}</Link>
                  <div className="text-xs text-muted-foreground md:hidden">{c.phone}</div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{c.phone}</td>
                <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{c.email}</td>
                <td className="pr-4"><ChevronRight className="h-4 w-4 text-slate-400" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
